"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Cloud, ShoppingBag, ShieldCheck, Code, CircleCheck, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  id: "salesforce",
  title: "Retail POS (iOS Focus)",
  colorText: "text-[#1A5A8E]",
  colorBg: "bg-[#1A5A8E]/10",
  linkUrl: "#",
  outcome1: "Metrc-integrated checkout",
  outcome2: "iPad-optimized interface",
  outcome3: "Real-time inventory sync"
}, {
  id: "shopify",
  title: "CRM & Engagement",
  colorText: "text-[#2A71A7]",
  colorBg: "bg-[#2A71A7]/10",
  linkUrl: "#",
  outcome1: "Automated loyalty programs",
  outcome2: "Targeted SMS marketing",
  outcome3: "Unified customer profiles"
}, {
  id: "fintech",
  title: "Compliance Workflows",
  colorText: "text-[#134B75]",
  colorBg: "bg-[#134B75]/10",
  linkUrl: "#",
  outcome1: "Automated Metrc reporting",
  outcome2: "Digital document automation",
  outcome3: "Regulatory audit trails"
}, {
  id: "fullstack",
  title: "AI Operations",
  colorText: "text-[#4092C7]",
  colorBg: "bg-[#4092C7]/10",
  linkUrl: "#",
  outcome1: "Demand forecasting",
  outcome2: "Intelligent staff scheduling",
  outcome3: "Automated reorder points"
}];
const getServiceIcon = (id: string, className: string) => {
  switch (id) {
    case "salesforce":
      return <ShoppingBag className={className} />;
    case "shopify":
      return <Cloud className={className} />;
    case "fintech":
      return <ShieldCheck className={className} />;
    case "fullstack":
      return <Code className={className} />;
    default:
      return <Cloud className={className} />;
  }
};
export default function SpecializedExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="147028" id="unified-platform-capabilities" className="dark bg-gradient-to-br from-[#1A5A8E] to-[#4092C7] py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-white/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-white/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div ref={ref} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block py-1 px-3 rounded-full bg-white/20 text-white text-sm font-semibold tracking-wider uppercase mb-4">
            Platform Ecosystem
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
            Unified Platform Capabilities
          </h2>
          <p className="text-lg text-white/90">
            The Salesforce solution for vertical cannabis operations, unifying retail, CRM, and compliance in one connected platform built for scale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {servicesData.map((service, i) => <div key={service.id} data-index={i} className={`transition-all duration-700 ease-out delay-[${i * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <Card className="h-full flex flex-col bg-white border-[#134B75]/20 rounded-[32px] shadow-sm hover:shadow-2xl hover:border-[#1A5A8E] transition-all duration-300 hover:-translate-y-[10px] group">
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${service.colorBg} ${service.colorText}`}>
                    {getServiceIcon(service.id, "w-7 h-7")}
                  </div>
                  <CardTitle className="text-xl font-bold text-[#0F172A] leading-tight">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow pb-6">
                  <ul className="space-y-3">
                    {[service.outcome1, service.outcome2, service.outcome3].map((outcome, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                          <CircleCheck className={`w-5 h-5 shrink-0 mt-0.5 ${service.colorText}`} />
                          <span className="text-[#0F172A]/70 text-sm leading-relaxed">
                            {outcome}
                          </span>
                        </li>)}
                  </ul>
                </CardContent>
                <CardFooter className="pt-0 mt-auto">
                  <Link to={service.linkUrl} className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:underline ${service.colorText}`}>
                    Explore Feature
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}